import { MarketingUsp } from '../../../domain/marketing-usp';
import { CreateMarketingUspPageItemRequestApiModel, GenericLayoutApiModel, MarketingUspPageItemApiModel } from '@cxb/editor-stb-api';
import { mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import {
  descriptionLocalizedMapperDomainToApi,
  platformTargetingMapper,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
  titleLocalizedFromHeadlineMapper,
} from '../../mapping/create-tray-domain-to-api';
import { commonMapper, scheduledDateMapperApiToDomain } from '../../mapping/create-tray-api-to-domain';
import { MarketingUspTrayFormViewModel } from './marketing-usp-tray-form.view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import { SchedulingType } from '../../../shared/enums/scheduling-type.enum';

// 'MarketingUsp'

/**
 * DOMAIN -> API
 */

export const mapDomainUspToApi = (
  uspTray: MarketingUsp,
  pageVersionId: string,

  tenantId?: string
): CreateMarketingUspPageItemRequestApiModel => {
  const commonProperties = mapperPipe(uspTray, scheduledDateMapperDomainToApi, targetingMapperDomainToApi, platformTargetingMapper);

  return remainderMapper(commonProperties, () => ({
    pageVersionId,
    layout: GenericLayoutApiModel.Centered,
    name: uspTray.titleEnglish,
    sellingPoints: uspTray.assets.map((asset, index) =>
      remainderMapper(mapperPipe(asset, titleLocalizedFromHeadlineMapper, descriptionLocalizedMapperDomainToApi), () => ({ order: index }))
    ),
    tenantId,
  }));
};

/**
 * API -> DOMAIN
 */

export const mapApiUspToDomain = (usp: MarketingUspPageItemApiModel): MarketingUsp => {
  return remainderMapper(mapperPipe(usp, commonMapper, scheduledDateMapperApiToDomain, targetingMapperDomainToApi), () => ({
    type: 'MarketingUsp',
    titleEnglish: usp.name,
    assets: usp.sellingPoints.map((it) => ({
      descriptionTextArabic: it.descriptionLocalized.ar,
      descriptionTextEnglish: it.descriptionLocalized.en,
      headlineTextArabic: it.titleLocalized.ar,
      headlineTextEnglish: it.titleLocalized.en,
    })),
  }));
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapDomainMarketingUspToViewModel = (item: MarketingUsp): MarketingUspTrayFormViewModel => {
  return {
    id: item.id,
    countryIds: item.countryIds,
    type: TrayFormViewModelType.USP,
    scheduling: {
      schedulingType: item.scheduledFrom || item.scheduledTo ? SchedulingType.Schedule : SchedulingType.LiveWhenPublished,
      scheduledFrom: item.scheduledFrom ? new Date(item.scheduledFrom).toISOString() : undefined,
      scheduledTo: item.scheduledTo ? new Date(item.scheduledTo).toISOString() : undefined,
    },
    moduleName: item.titleEnglish,
    userSegmentIds: item.userSegmentIds,
    assets: item.assets.map((asset) => ({
      headlineText: { en: asset.headlineTextEnglish, ar: asset.headlineTextArabic },
      description: { en: asset.descriptionTextEnglish, ar: asset.descriptionTextArabic },
    })),
  };
};

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapViewModelUspToDomain = (
  marketingUspViewModel: MarketingUspTrayFormViewModel,
  platformIds: Record<PlatformType, string>
): MarketingUsp => {
  return {
    id: marketingUspViewModel.id,
    scheduledFrom: marketingUspViewModel.scheduling?.scheduledFrom
      ? new Date(marketingUspViewModel.scheduling.scheduledFrom).getTime()
      : undefined,
    scheduledTo: marketingUspViewModel.scheduling?.scheduledTo
      ? new Date(marketingUspViewModel.scheduling.scheduledTo).getTime()
      : undefined,
    titleEnglish: marketingUspViewModel.moduleName,
    userSegmentIds: marketingUspViewModel.userSegmentIds,
    countryIds: marketingUspViewModel.countryIds,
    type: 'MarketingUsp',
    order: undefined,
    visible: undefined,
    draggable: true,
    assets: marketingUspViewModel.assets.map((usp) => ({
      headlineTextEnglish: usp.headlineText.en,
      headlineTextArabic: usp.headlineText.ar,
      descriptionTextEnglish: usp.description.en,
      descriptionTextArabic: usp.description.ar,
    })),
    platformIds: Object.values(platformIds),
  };
};
